import React from 'react';
import { Box, Typography, Button, Paper, Stack, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    IconLock, 
    IconUserPlus, 
    IconAlertCircle,
    IconArrowRight,
    IconShieldLock
} from '@tabler/icons-react';
import { colors, fonts } from '../../ColorFont/color';
import BlockedView from '../common/BlockedView';

const CarbonCreditsAuthGuard = ({ children, userData, firebaseUser, loading }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // 1. Loading State
    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '60vh',
                gap: 2
            }}>
                <CircularProgress sx={{ color: colors.primary }} />
                <Typography sx={{ fontFamily: fonts.poppins, color: colors.text }}>
                    {t('common:loading')}
                </Typography>
            </Box>
        );
    }

    // 2. Auth State: Not logged in to Firebase
    if (!firebaseUser) {
        return <BlockedView 
            icon={<IconLock size={48} stroke={1.5} color={colors.primary} />}
            title={t('common:carbonCredits.authGuardTitle')}
            description={t('common:carbonCredits.authGuardLoginRequired')}
            buttonText={t('common:carbonCredits.authGuardLoginBtn')}
            onAction={() => {
                // Trigger login dialog via URL param
                const currentUrl = new URL(window.location);
                currentUrl.searchParams.set('openLogin', 'true');
                window.location.href = currentUrl.toString();
            }}
        />;
    }

    // 3. Profile State: Logged in but no userData (not in MongoDB)
    if (!userData) {
        return <BlockedView 
            icon={<IconUserPlus size={48} stroke={1.5} color={colors.primary} />}
            title={t('common:carbonCredits.authGuardTitle')}
            description={t('common:carbonCredits.authGuardProfileRequired')}
            buttonText={t('common:carbonCredits.authGuardProfileBtn')}
            onAction={() => navigate('/userform')}
        />;
    }

    // 4. Role State: Not a Farmer/Seller
    const allowedRoles = ['Seller', 'Farmer', 'Supplier', 'Admin'];
    if (!allowedRoles.includes(userData.role)) {
        return <BlockedView 
            icon={<IconShieldLock size={48} stroke={1.5} color="#FF9433" />}
            title={t('common:carbonCredits.authGuardFarmersOnly')}
            description={t('common:carbonCredits.authGuardRestricted')}
            buttonText={t('common:goToHomeBtn')}
            onAction={() => navigate('/dashboard')}
        />;
    }

    // If all checks pass, render children
    return <>{children}</>;
};

export default CarbonCreditsAuthGuard;
